import { Spinner } from '@consigli/facade';
import {
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@consigli/shadcn';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState, Fragment, ReactNode } from 'react';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading: boolean;
  onRowClick?: (row: TData) => void;
  scrollable?: boolean;
  height?: string;
  expandedContent?: (row: TData) => ReactNode;
  expandOnRowClick?: boolean;
  rowClassName?: (row: TData) => string | undefined;
}

export const DataTable = <TData, TValue>({
  columns,
  data,
  isLoading,
  onRowClick,
  scrollable = false,
  height = 'auto',
  expandedContent,
  expandOnRowClick = false,
  rowClassName,
}: DataTableProps<TData, TValue>) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
  });

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const tableContent = (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className="hover:bg-transparent">
            {expandedContent && <TableHead className="w-12 font-semibold"></TableHead>}
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} className="font-semibold">
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            if (expandedContent) {
              const isExpanded = expandedRows.has(row.id);
              const isRowClickable = expandOnRowClick || onRowClick;
              return (
                <Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className={`hover:bg-gray-100 ${isRowClickable ? 'cursor-pointer' : ''} ${rowClassName?.(row.original) ?? ''}`}
                    onClick={() => {
                      if (expandOnRowClick) {
                        toggleRowExpansion(row.id);
                      }
                      onRowClick?.(row.original);
                    }}
                  >
                    <TableCell className="w-12">
                      <button
                        onClick={(e) => {
                          if (!expandOnRowClick) {
                            e.stopPropagation();
                            toggleRowExpansion(row.id);
                          }
                        }}
                        className={`p-1 inline-flex ${!expandOnRowClick ? 'hover:bg-gray-200 rounded cursor-pointer' : ''}`}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </button>
                    </TableCell>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} className="p-0">
                        <div className="border-t bg-gray-50 p-4">
                          {expandedContent(row.original)}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            } else {
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={`hover:bg-gray-100 ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(row.original) ?? ''}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            }
          })
        ) : (
          <TableRow>
            <TableCell
              colSpan={columns.length + (expandedContent ? 1 : 0)}
              className="h-24 text-center"
            >
              <div className="flex justify-center items-center h-full">
                {isLoading ? <Spinner /> : 'No results.'}
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  if (scrollable) {
    return (
      <div className="overflow-hidden relative" style={{ height }}>
        {isLoading && data.length > 0 && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
            <Spinner />
          </div>
        )}
        <ScrollArea className="h-full">
          {tableContent}
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto relative">
      {isLoading && data.length > 0 && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <Spinner />
        </div>
      )}
      {tableContent}
    </div>
  );
};
